/** Private, bounded memory cache. No signed URLs or credentials are persisted. */
export interface DownloadPhoto {
  id: string;
  role: "label" | "back_label";
  url: string;
  expires_at: string;
}

interface Entry {
  photos?: DownloadPhoto[];
  expiresAt: number;
  pending?: Promise<DownloadPhoto[]>;
}

const entries = new Map<string, Entry>();
let session: string | null | undefined;
let generation = 0;
let running = 0;
const waiting: Array<() => void> = [];

export function clearPhotoCache(): void {
  generation += 1;
  entries.clear();
}

export function invalidatePhotos(): void {
  clearPhotoCache();
  window.dispatchEvent(new Event("wineps:photos-invalidated"));
}

export function setPhotoSession(token: string | null): void {
  if (session === token) return;
  session = token;
  clearPhotoCache();
}

async function scheduled<T>(load: () => Promise<T>): Promise<T> {
  // A released slot is reserved for the next waiter before another caller may acquire it.
  if (running >= 4) await new Promise<void>(resolve => waiting.push(resolve));
  else running += 1;
  try { return await load(); }
  finally {
    const next = waiting.shift();
    if (next) next();
    else running -= 1;
  }
}

export function cachedPhotos(
  token: string, key: string, load: () => Promise<DownloadPhoto[]>, force = false,
): Promise<DownloadPhoto[]> {
  if (session === undefined) setPhotoSession(token);
  if (session !== token) return Promise.reject(new Error("La session photo a changé."));
  const found = entries.get(key);
  if (found?.pending) return found.pending;
  if (!force && found?.photos && found.expiresAt > Date.now() + 30_000) {
    entries.delete(key);
    entries.set(key, found);
    return Promise.resolve(found.photos);
  }
  const epoch = generation;
  const entry: Entry = { expiresAt: 0 };
  const pending = scheduled(async () => {
    if (epoch !== generation) throw new Error("Le chargement photo a été annulé.");
    const photos = await load();
    if (epoch !== generation) throw new Error("Le chargement photo a été annulé.");
    entry.photos = photos;
    entry.expiresAt = photos.length
      ? Math.min(...photos.map(photo => Date.parse(photo.expires_at)))
      : Date.now() + 60_000;
    // Bound retained data without evicting in-flight work and breaking deduplication.
    for (const [oldKey, oldEntry] of entries) {
      if (entries.size <= 200) break;
      if (!oldEntry.pending && oldKey !== key) entries.delete(oldKey);
    }
    return photos;
  });
  entry.pending = pending;
  entries.set(key, entry);
  void pending.then(
    () => { entry.pending = undefined; },
    () => { if (entries.get(key) === entry) entries.delete(key); },
  );
  return pending;
}
